/**
 * This file is segregated from selectors.ts to avoid a missing Crypto dependency on the Mobile app package.
 * Soon, once we solve that dependency issue, we will be able to remove this file and use selectors.ts directly
 */

import {includes} from 'ramda'

import {safeJsonParse} from '../../utils/object'
import {
  CustomAppDataAttributes,
  User,
  UserAttributeIdentities
} from '../types/Cognito'
import {StateType} from './slice'

export enum UserTier {
  Employee = 'employee',
  Plus = 'plus',
  FriendsAndFamily = 'fsf'
}

/**
 * Get stringified Cognito's custom:app_data attribute, and return as object
 */
export const getCognitoCustomAppData = (
  user?: StateType['user']
): CustomAppDataAttributes =>
  safeJsonParse(user?.attributes?.['custom:app_data'])

/**
 * Get the current Cognito user tier.
 * Different tiers can be offered different rates and experiences
 * @param user - Cognito user
 * @returns ['employee', 'plus']
 */
export const getCognitoUserTier = (user?: StateType['user']) =>
  getCognitoCustomAppData(user)?.tier ?? []

export enum Connections {
  Google = 'google-oauth2',
  Facebook = 'facebook',
  Apple = 'apple',
  OIDCFederation = 'oidc-federation'
}

export const getConnection = (user?: User | null) => {
  if (!user?.attributes?.identities) {
    return null
  }

  const identities = safeJsonParse<UserAttributeIdentities[]>(
    user?.attributes?.identities
  )
  const providerName = identities?.[0]?.providerName

  // `partner-OIDC` is the shared Cognito federation provider name reused
  // by every brand with `auth.hasOidcProvider` enabled — see
  // `auth/modules/thunks.ts` for the outbound counterpart.
  const providerNameMap: Record<string, Connections> = {
    Google: Connections.Google,
    signInWithApple: Connections.Apple,
    SignInWithApple: Connections.Apple,
    Facebook: Connections.Facebook,
    'partner-OIDC': Connections.OIDCFederation
  }

  return providerNameMap[providerName] || null
}

/**
 * Discover if the current user is in the employee tier.
 * @param userTier - User tier, eg: ['employee', 'plus']
 * @returns boolean
 */
export const getIsUserTierEmployee = (userTier: UserTier[]) =>
  includes(UserTier.Employee, userTier) ||
  includes(UserTier.FriendsAndFamily, userTier)

/**
 * Discover if the current user is in the plus tier.
 * @param userTier - User tier, eg: ['employee', 'plus']
 * @returns boolean
 */
export const getIsUserTierPlus = (userTier: UserTier[]) =>
  includes(UserTier.Plus, userTier)
